import React, { useState } from "react";
import { LoadingButton } from "@mui/lab";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { httpService } from "src/Helpers/utils/axios";
import { toast } from "src/Toaster/Toaster";
import type {
	IOfflineExhibitorListItem,
	IOfflineStall,
} from "../type/offlineExhibitorType";

interface Props {
	searchQuery?: string;
	totalCount?: number;
	currentData?: IOfflineExhibitorListItem[];
	isLoading?: boolean;
}

const formatCategory = (cat?: string[] | string): string => {
	if (!cat) return "-";
	if (Array.isArray(cat)) return cat.join(", ");
	if (typeof cat === "string" && cat.trim()) {
		try {
			const parsed = JSON.parse(cat);
			if (Array.isArray(parsed)) return parsed.join(", ");
			return cat;
		} catch {
			return cat;
		}
	}
	return String(cat);
};

const getStallNos = (stalls?: IOfflineStall[]): string => {
	if (!stalls || !Array.isArray(stalls) || stalls.length === 0) return "-";
	const nos = stalls.map((s) => s.stall_no).filter(Boolean);
	return nos.length > 0 ? nos.join(", ") : "-";
};

const getHallNames = (stalls?: IOfflineStall[]): string => {
	if (!stalls || !Array.isArray(stalls) || stalls.length === 0) return "-";
	const halls = stalls
		.map((s) => s.hall_name)
		.filter(Boolean)
		.filter((v, i, a) => a.indexOf(v) === i);
	return halls.length > 0 ? halls.join(", ") : "-";
};

const ExportOfflineExhibitorsExcel: React.FC<Props> = ({
	searchQuery,
	totalCount,
	currentData,
	isLoading,
}) => {
	const [isExporting, setIsExporting] = useState<boolean>(false);

	const handleExportExcel = async () => {
		try {
			setIsExporting(true);

			let exhibitors: IOfflineExhibitorListItem[] = [];

			// If currentData already contains all items and totalCount is equal, use it;
			// otherwise fetch all from API without pagination limit.
			if (
				currentData &&
				totalCount !== undefined &&
				currentData.length >= totalCount &&
				currentData.length > 0
			) {
				exhibitors = currentData;
			} else {
				const res = await httpService.get<IOfflineExhibitorListItem[]>({
					url: "/api/v1/admin/offline-exhibitor",
					params: {
						limit: totalCount && totalCount > 0 ? totalCount : 5000,
						skip: 0,
						...(searchQuery?.trim()
							? { searchQuery: searchQuery.trim() }
							: {}),
					},
				});

				exhibitors = Array.isArray(res?.data) ? res.data : [];
			}

			if (!exhibitors || exhibitors.length === 0) {
				toast.fire({
					icon: "warning",
					title: "No exhibitor data available to export!",
				});
				return;
			}

			const workbook = new Excel.Workbook();
			const worksheet = workbook.addWorksheet("Offline Exhibitors");

			// Define columns
			worksheet.columns = [
				{ header: "SL", key: "sl", width: 8 },
				{ header: "Exhibitor No", key: "exhibitor_no", width: 18 },
				{ header: "Company Name", key: "name", width: 32 },
				{ header: "Email", key: "email", width: 28 },
				{ header: "Company Email", key: "company_email", width: 28 },
				{ header: "Category", key: "category", width: 26 },
				{ header: "Stall No", key: "stall_no", width: 20 },
				{ header: "Hall Name", key: "hall_name", width: 24 },
				{ header: "Total Scanned Stalls", key: "total_scanned", width: 22 },
			];

			// Style header row
			const headerRow = worksheet.getRow(1);
			headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
			headerRow.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FF2E7D32" }, // Forest Green theme
			};
			headerRow.alignment = { vertical: "middle", horizontal: "center" };
			headerRow.height = 28;

			// Add data rows
			exhibitors.forEach((exhibitor, index) => {
				const stallNos = getStallNos(exhibitor.stalls);
				const hallNames = getHallNames(exhibitor.stalls);

				const row = worksheet.addRow({
					sl: index + 1,
					exhibitor_no: exhibitor.exhibitor_no || "-",
					name: exhibitor.name || "-",
					email: exhibitor.email || "-",
					company_email: exhibitor.company_email || "-",
					category: formatCategory(exhibitor.category),
					stall_no: stallNos,
					hall_name: hallNames,
					total_scanned:
						exhibitor.total_scanned_stalls ??
						exhibitor.total_scanned_stall ??
						0,

				});

				row.alignment = { vertical: "middle" };
				row.height = 22;

				// Center align key identifier cells
				row.getCell("sl").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("exhibitor_no").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("stall_no").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("hall_name").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("total_scanned").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
			});

			// Add cell borders
			worksheet.eachRow((row) => {
				row.eachCell((cell) => {
					cell.border = {
						top: { style: "thin", color: { argb: "FFE0E0E0" } },
						left: { style: "thin", color: { argb: "FFE0E0E0" } },
						bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
						right: { style: "thin", color: { argb: "FFE0E0E0" } },
					};
				});
			});

			const buffer = await workbook.xlsx.writeBuffer();
			saveAs(
				new Blob([buffer]),
				`Offline_Exhibitors_${moment().format("YYYY-MM-DD")}.xlsx`
			);

			toast.fire({
				icon: "success",
				title: "Exhibitors exported successfully!",
			});
		} catch (error: any) {
			console.error("Failed to export Excel:", error);
			toast.fire({
				icon: "error",
				title: error?.message || "Failed to export Excel",
			});
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<LoadingButton
			variant="outlined"
			size="small"
			loading={isExporting}
			disabled={isLoading || totalCount === 0}
			startIcon={<DownloadTwoToneIcon />}
			onClick={handleExportExcel}
			sx={{
				borderRadius: 1,
				textTransform: "none",
				fontWeight: 600,
				fontSize: "0.8125rem",
				px: 1.8,
				py: 0.7,
				whiteSpace: "nowrap",
			}}
		>
			Export Excel
		</LoadingButton>
	);
};

export default ExportOfflineExhibitorsExcel;
